// src/pages/Auth/AuthPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { AutoComplete } from 'antd';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { loginRequest } from '../../authConfig';
import Cookies from 'js-cookie';
import Fuse from 'fuse.js';
import '../../styles/auth.css';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const API_BASE_URL = 'https://api1.ororigin.xyz'; // 后端 API 基础 URL

function AuthPage() {
    useDocumentTitle("天地源 | 登录");
    const { instance, accounts, inProgress } = useMsal();
    const navigate = useNavigate();
    
    const [step, setStep] = useState('checking');
    const [verificationToken, setVerificationToken] = useState('');
    const [username, setUsername] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [userInfo, setUserInfo] = useState(null);
    const [uid, setUid] = useState('');
    const [memberType, setMemberType] = useState(''); // 'old' 或 'new'
    const [reservedAccounts, setReservedAccounts] = useState([]); // 预留账户列表
    const [selectedReservedAccount, setSelectedReservedAccount] = useState(''); // 选择的预留账户
    const [query, setQuery] = useState(''); // 搜索查询词
    
    // 创建 Fuse 实例
    const fuse = useMemo(() => {
        if (reservedAccounts.length === 0) {
            return null;
        }
        return new Fuse(reservedAccounts, {
            keys: ['name'],
            threshold: 0.5,
            minMatchCharLength: 1,
            includeScore: true,
        });
    }, [reservedAccounts]);
    
    // 搜索处理
    const searchResult = useMemo(() => {
        if (!query.trim() || !fuse) {
            
            return [...reservedAccounts].sort((a, b) =>
                a.localeCompare(b, "zh-CN")
            );
        }
        const fuseResults = fuse.search(query);
        const sortedResults = fuseResults.sort((a, b) => {
            if (Math.abs(a.score - b.score) > 0.2) {
                return a.score - b.score;
            }
            return a.item.localeCompare(b.item, "zh-CN");
        });
        
        return sortedResults.map((result) => result.item);
    }, [query, fuse, reservedAccounts]);
    
    // 转换为 AutoComplete 选项格式
    const searchOptions = useMemo(() => {
        return searchResult.map((item, index) => ({
            value: item,
            label: item,
            key: index,
        }));
    }, [searchResult]);
    
    const checkAuthStatus = useCallback(() => {
        if (inProgress === 'startup' || inProgress === 'handleRedirect') {
            setStep('checking');
            return;
        }
        
        if (accounts.length > 0) {
            const account = instance.getActiveAccount() || accounts[0];
            const userData = {
                account: {
                    username: account.username,
                    name: account.name,
                    idTokenClaims: account.idTokenClaims,
                    idToken: account.idToken
                }
            };
            setUserInfo(userData);
            
            if (step === 'checking' || step === 'login') {
                setStep('token');
            }
        } else {
            setStep('login');
        }
    }, [accounts, instance, inProgress, step]);
    
    useEffect(() => {
        checkAuthStatus();
    }, [checkAuthStatus]);
    
    const handleMicrosoftLogin = () => {
        setError('');
        
        if (inProgress !== 'none') {
            return;
        }
        
        setIsLoading(true);
        
        instance.loginRedirect(loginRequest).catch((error) => {
            setIsLoading(false);
            
            let errorMsg = '登录失败，请重试';
            if (error.errorCode === 'user_cancelled') {
                errorMsg = '用户取消了登录';
            } else if (error.errorCode === 'interaction_in_progress') {
                errorMsg = '登录操作正在进行中，请稍候';
            }
            setError(errorMsg);
        });
    };
    
    const handleTokenSubmit = async (e) => {
        e.preventDefault();
        if (!verificationToken.trim()) {
            setError('请输入验证令牌');
            return;
        }
        
        if (!userInfo?.account) {
            setError('未找到用户信息，请重新登录');
            return;
        }
        
        setIsLoading(true);
        setError('');
        
        try {
            const requestData = {
                microsoftAccount: {
                    username: userInfo.account.username,
                    name: userInfo.account.name,
                    idTokenClaims: userInfo.account.idTokenClaims,
                    idToken: userInfo.account.idToken
                },
                verificationToken: verificationToken.trim()
            };
            
            const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData)
            });
            
            const data = await response.json();
            
            if (response.ok && data.success) {
                if (data.needUsername) {
                    // 首次登录，需要设置用户名
                    setUid(data.uid || '');
                    // 获取预留账户列表
                    await fetchReservedAccounts();
                    // 进入选择成员类型步骤
                    setStep('memberType');
                } else {
                    // 已有用户名，直接登录成功
                    setUid(data.uid || '');
                    Cookies.set('uid', data.uid, { expires: 7 }); // 设置 7 天过期
                    setStep('complete');
                    
                    setTimeout(() => {
                        navigate('/', { replace: true });
                    }, 1500);
                }
            } else {
                setError(data.message || '验证失败');
            }
        } catch (error) {
            setError('网络错误，请重试');
            console.error('验证失败:', error);
        } finally {
            setIsLoading(false);
        }
    };
    
    const fetchReservedAccounts = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/usersasasa/reserved`);
            const data = await response.json();
            
            if (response.ok && data.success) {
                setReservedAccounts(data.reserved_accounts || []);
            } else {
                console.error('获取预留账户失败:', data.message);
            }
        } catch (error) {
            console.error('获取预留账户失败:', error);
        }
    };
    
    const handleMemberTypeSelect = (type) => {
        setMemberType(type);
        setError('');
        
        if (type === 'old') {
            // 如果是老成员，进入选择预留账户步骤
            setStep('selectReservedAccount');
        } else {
            // 如果是新成员，进入输入用户名步骤
            setStep('newUsername');
        }
    };
    
    const handleReservedAccountSubmit = async (e) => {
        e.preventDefault();
        if (!selectedReservedAccount) {
            setError('请选择一个预留账户');
            return;
        }
        
        await submitUsername(selectedReservedAccount, 'old');
    };
    
    const handleNewUsernameSubmit = async (e) => {
        e.preventDefault();
        if (!username.trim()) {
            setError('请输入用户名');
            return;
        }
        
        await submitUsername(username.trim(), 'new');
    };
    
    const submitUsername = async (usernameToSubmit, type) => {
        if (!userInfo?.account) {
            setError('未找到用户信息，请重新登录');
            return;
        }
        
        setIsLoading(true);
        setError('');
        
        try {
            const requestData = {
                microsoftAccount: {
                    username: userInfo.account.username,
                    name: userInfo.account.name,
                    idTokenClaims: userInfo.account.idTokenClaims,
                    idToken: userInfo.account.idToken
                },
                username: usernameToSubmit,
                memberType: type
            };
            
            const response = await fetch(`${API_BASE_URL}/api/auth/set-username`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData)
            });
            
            const data = await response.json();
            
            if (response.ok && data.success) {
                setUid(data.uid || '');
                Cookies.set('uid', data.uid, { expires: 7 }); // 设置 7 天过期
                setStep('complete');
                
                setTimeout(() => {
                    navigate('/', { replace: true });
                }, 1500);
            } else {
                setError(data.message || '用户名设置失败');
            }
        } catch (error) {
            setError('网络错误，请重试');
            console.error('设置用户名失败:', error);
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleLogout = () => {
        // 清除 cookie
        Cookies.remove('uid');
        instance.logoutRedirect().catch(console.error);
    };
    
    // 如果需要，可以添加一个函数来检查 UID 是否有效
    const checkUidValid = async (uid) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/user/validate/${uid}`);
            const data = await response.json();
            return data.success && data.is_valid;
        } catch (error) {
            console.error('检查 UID 失败:', error);
            return false;
        }
    };
    
    // 可以添加一个 useEffect 来检查是否已有有效的 UID cookie
    useEffect(() => {
        const existingUid = Cookies.get('uid');
        if (existingUid) {
            // 如果已有 UID，可以检查其有效性
            checkUidValid(existingUid).then(isValid => {
                if (isValid) {
                    // UID 有效，直接跳转到首页
                    navigate('/', { replace: true });
                } else {
                    // UID 无效，清除 cookie
                    Cookies.remove('uid');
                }
            });
        }
    }, [navigate]);
    
    const renderStepContent = () => {
        switch (step) {
            case 'checking':
                return (
                    <div className="auth-loading">
                        <div className="auth-loading-spinner"></div>
                        <h4 className="mt-4">正在检查登录状态...</h4>
                        <p className="auth-hint">请稍候</p>
                    </div>
                );
                
            case 'login':
                return (
                    <div className='auth-body-login'>
                        {error && (
                            <div className={`auth-alert auth-alert-error auth-login-error`}>
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                {error}
                            </div>
                        )}
                        
                        <button 
                            onClick={handleMicrosoftLogin}
                            disabled={isLoading || inProgress !== 'none'}
                            className="auth-button auth-button-primary"
                        >
                            {isLoading || inProgress !== 'none' ? (
                                <>
                                    <i className="fas fa-spinner fa-spin me-2"></i>
                                    {inProgress === 'login' ? '正在登录...' : '准备登录...'}
                                </>
                            ) : (
                                <>
                                    <i className="fab fa-microsoft me-2"></i>
                                    使用 Microsoft 账户登录
                                </>
                            )}
                        </button>
                    </div>
                );
                
            case 'token':
                return (
                    <div className="auth-step-content">
                        <div className="auth-account-info">
                            <div className="auth-account-avatar">
                                <i className="fas fa-user"></i>
                            </div>
                            <div className="auth-account-details">
                                <h6>已登录账户</h6>
                                <p>{userInfo?.account?.name || userInfo?.account?.username}</p>
                            </div>
                        </div>
                        
                        {error && (
                            <div className={`auth-alert auth-alert-error`}>
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                {error}
                            </div>
                        )}
                        
                        <Form onSubmit={handleTokenSubmit}>
                            <div className="auth-form-group">
                                <label className="auth-form-label">验证令牌</label>
                                <input
                                    type="password"
                                    placeholder=" 请输入验证令牌"
                                    value={verificationToken}
                                    onChange={(e) => setVerificationToken(e.target.value)}
                                    disabled={isLoading}
                                    className="auth-form-input"
                                />
                                <p className="auth-hint">请从群公告中获取验证令牌</p>
                            </div>
                            
                            <div className="auth-buttons-group">
                                <button 
                                    type="button"
                                    onClick={handleLogout}
                                    disabled={isLoading}
                                    className="auth-button auth-button-secondary"
                                >
                                    切换账户
                                </button>
                                <button 
                                    type="submit"
                                    disabled={isLoading}
                                    className="auth-button auth-button-primary"
                                >
                                    {isLoading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin me-2"></i>
                                            验证中...
                                        </>
                                    ) : '验证并继续'}
                                </button>
                            </div>
                        </Form>
                    </div>
                );
                
            case 'memberType':
                return (
                    <div className="auth-step-content">
                        <div className="auth-alert auth-alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            你是老资历还是刚加入公会的萌新？
                        </div>
                        
                        {error && (
                            <div className={`auth-alert auth-alert-error`}>
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                {error}
                            </div>
                        )}
                        
                        <div className="auth-member-type-options">
                            <button 
                                onClick={() => handleMemberTypeSelect('old')}
                                disabled={isLoading}
                                className="auth-button auth-button-primary auth-member-type-button"
                            >
                                <i className="fas fa-user-friends me-2"></i>
                                我是老成员
                            </button>
                            
                            <button 
                                onClick={() => handleMemberTypeSelect('new')}
                                disabled={isLoading}
                                className="auth-button auth-button-primary-2 auth-member-type-button"
                            >
                                <i className="fas fa-user-plus me-2"></i>
                                我是新成员
                            </button>
                        </div>
                        
                        <div className="auth-buttons-group">
                            <button 
                                type="button"
                                onClick={() => setStep('token')}
                                disabled={isLoading}
                                className="auth-button auth-button-secondary"
                            >
                                返回
                            </button>
                        </div>
                    </div>
                );
                
            case 'selectReservedAccount':
                return (
                    <div className="auth-step-content">
                        <div className="auth-alert auth-alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            预留账户的用户名可以在个人页面修改
                        </div>
                        
                        {error && (
                            <div className={`auth-alert auth-alert-error`}>
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                {error}
                            </div>
                        )}
                        
                        <Form onSubmit={handleReservedAccountSubmit}>
                            <div className="auth-form-group">
                                <label className="auth-form-label">还记得你是谁吗？</label>
                                <AutoComplete
                                    getPopupContainer={(triggerNode) => triggerNode.parentElement}
                                    className="auth-form-input"
                                    options={searchOptions}
                                    showSearch={{ onSearch: (v) => setQuery(v) }}
                                    value={selectedReservedAccount}
                                    onChange={(v) => setSelectedReservedAccount(v)}
                                    onSelect={(value, option) => {
                                        setSelectedReservedAccount(option.label);
                                    }}
                                    placeholder="请选择或输入预留账户"
                                    disabled={isLoading}
                                />
                                <p className="auth-hint">别乱选啊！我不想上线改数据库qwq</p>
                            </div>
                            
                            <div className="auth-buttons-group">
                                <button 
                                    type="button"
                                    onClick={() => setStep('memberType')}
                                    disabled={isLoading}
                                    className="auth-button auth-button-secondary"
                                >
                                    返回
                                </button>
                                <button 
                                    type="submit"
                                    disabled={isLoading || !selectedReservedAccount}
                                    className="auth-button auth-button-primary"
                                >
                                    {isLoading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin me-2"></i>
                                            保存中...
                                        </>
                                    ) : '完成设置'}
                                </button>
                            </div>
                        </Form>
                    </div>
                );
                
            case 'newUsername':
                return (
                    <div className="auth-step-content">
                        <div className="auth-alert auth-alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            请设置一个用户名用于系统内显示
                        </div>
                        
                        {error && (
                            <div className={`auth-alert auth-alert-error`}>
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                {error}
                            </div>
                        )}
                        
                        <Form onSubmit={handleNewUsernameSubmit}>
                            <div className="auth-form-group">
                                <label className="auth-form-label">用户名</label>
                                <input
                                    type="text"
                                    placeholder="请输入用户名"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    disabled={isLoading}
                                    className="auth-form-input"
                                />
                                <p className="auth-hint">用户名将显示在系统内，可随时在设置中修改</p>
                            </div>
                            
                            <div className="auth-buttons-group">
                                <button 
                                    type="button"
                                    onClick={() => setStep('memberType')}
                                    disabled={isLoading}
                                    className="auth-button auth-button-secondary"
                                >
                                    返回
                                </button>
                                <button 
                                    type="submit"
                                    disabled={isLoading}
                                    className="auth-button auth-button-primary"
                                >
                                    {isLoading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin me-2"></i>
                                            保存中...
                                        </>
                                    ) : '完成设置'}
                                </button>
                            </div>
                        </Form>
                    </div>
                );
                
            case 'complete':
                return (
                    <div className="auth-complete">
                        <div className="auth-complete-icon">
                            <i className="fas fa-check"></i>
                        </div>
                        
                        <h4 className="mb-3">登录成功！</h4>
                        <p className="auth-hint mb-4">
                            正在为您跳转到系统首页...
                        </p>
                        
                        <div className="auth-loading-spinner"></div>
                    </div>
                );
                
            default:
                return null;
        }
    };
    
    const getHeaderTitle = () => {
        switch (step) {
            case 'login':
                return '登录';
            case 'token':
                return '权限验证';
            case 'memberType':
                return '你是...';
            case 'selectReservedAccount':
                return '选择预留账户';
            case 'newUsername':
                return '设置用户名';
            default:
                return '登录';
        }
    };
    
    const getHeaderSubtitle = () => {
        switch (step) {
            case 'login':
                return 'ACCOUNT LOGIN';
            case 'token':
                return 'PERMISSION CHECK';
            case 'memberType':
                return 'WHO YOU ARE ?';
            case 'selectReservedAccount':
                return 'CHOOSE RESERVED ACCOUNT';
            case 'newUsername':
                return 'SET USERNAME';
            default:
                return 'ACCOUNT LOGIN';
        }
    };
    
    const getCardClassName = () => {
        const baseClass = 'auth-card';
        if (step === 'login') return baseClass;
        return `${baseClass} auth-card-auto-height`;
    };
    
    return (
        <div className="auth-container">
            <div className={getCardClassName()}>
                <div className='logo'>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        className="auth-logo-icon"
                    >
                        <defs>
                            <style>
                                {`
                                    .st0, .st1, .st2, .st3 {
                                        stroke: #7899df;
                                        stroke-miterlimit: 10;
                                    }
                                    .st0, .st3 {
                                        fill: none;
                                    }
                                    .st1 {
                                        fill: #dfb978;
                                    }
                                    .st2{
                                        fill: #7899df;
                                    }
                                    .st3 {
                                        stroke-linecap: round;
                                    }
                                `}
                            </style>
                        </defs>
                        <path className="st1" d="M-11.25-44.03" />
                        <g>
                            <path className="st0" d="M13.96,1.26" />
                            <polyline
                                className="st0"
                                points="7.85 13.9 13.42 11.82 13.42 5.49 8.06 7.46"
                            />
                            <polyline className="st0" points="2.77 7.76 2.77 5.64 7.9 7.5" />
                            <line className="st0" x1="2.77" y1="8.59" x2="2.77" y2="7.51" />
                            <line className="st0" x1="2.77" y1="10.06" x2="2.77" y2="8.59" />
                            <polyline
                                className="st0"
                                points="8.21 13.9 2.77 11.75 2.77 10.06"
                            />
                            <line className="st3" x1="8.12" y1="7.51" x2="8.12" y2="13.8" />
                            <line className="st3" x1="8.04" y1="2.71" x2="13.42" y2="4.83" />
                            <line className="st3" x1="7.94" y1="2.68" x2="2.76" y2="4.97" />
                            <polygon
                                className="st2"
                                points="8.54 13.46 13.3 11.56 13.24 5.56 8.44 7.44 8.54 13.46"
                            />
                            <polygon
                                className="st2"
                                points="4.33 10.49 6.36 11.31 6.39 8.58 4.3 7.79 4.33 10.49"
                            />
                        </g>
                    </svg>
                    <p className="auth-logo-name">天地源</p>
                </div>
                
                <div className='auth-header'>
                    <p className='auth-header-title'>{getHeaderTitle()}</p>
                    <p className='auth-header-subtitle'>{getHeaderSubtitle()}</p>
                </div>
                
                <div className="auth-body">
                    {renderStepContent()}
                </div>
            </div>
        </div>
    );
}

export default AuthPage;